import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../../../axios-client';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { PiPlusBold } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import MyPagination from '../../../components/MyPagination';
import { useStateContext } from '../../../context/ContextProvider';
import {
    IconButton,
    Flex,
    Box,
    Button,
    Stack,
    Table,
    Thead,
    InputGroup,
    InputLeftElement,
    Tbody,
    Tr,
    Th,
    Td,
    Input,
    FormControl,
    FormLabel,
    useToast,
    TableContainer,
    useColorModeValue,
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Alert,
    AlertIcon,
    Text,
    Skeleton,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Textarea
} from '@chakra-ui/react';

export const GestTutorielsAdmin = () => {
    const { t } = useTranslation();
    const [loadingData, setLoadingData] = useState(true);
    const [checkLengthData, setCheckLengthData] = useState(false);
    const { user } = useStateContext();
    const navigate = useNavigate();
    const toast = useToast();

    // Modal state for Add/Edit
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isEditMode, setIsEditMode] = useState(false);

    // Alert Dialog state for Delete
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [selectedTutoId, setSelectedTutoId] = useState(null);
    const cancelRef = React.useRef();

    // Search and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(5);
    const [paginationData, setPaginationData] = useState(null);

    // Form fields
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [youtubeLink, setYoutubeLink] = useState('');
    const [formError, setFormError] = useState('');

    // List data
    const [data, setData] = useState([]);
    const [selectedTuto, setSelectedTuto] = useState(null);

    const validateYoutubeLink = (url) => {
        const regExp = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
        return regExp.test(url);
    };

    // Load data
    const fetchTutoriels = useCallback(() => {
        setLoadingData(true);
        axiosClient.get(`/tutoriels/getAllTutoriels/?page_size=${pageSize}&page=${currentPage + 1}&search_term=${searchTerm}`)
            .then((response) => {
                const results = response.data.results || [];
                setPaginationData(response.data);
                setData(results);
                setCheckLengthData(results.length === 0);
                setLoadingData(false);
            })
            .catch(() => {
                setLoadingData(false);
            });
    }, [pageSize, currentPage, searchTerm]);

    useEffect(() => {
        fetchTutoriels();
    }, [fetchTutoriels]);

    const handleOpenAddModal = () => {
        setIsEditMode(false);
        setTitle('');
        setDescription('');
        setYoutubeLink('');
        setFormError('');
        onOpen();
    };

    const handleOpenEditModal = (tuto) => {
        setIsEditMode(true);
        setSelectedTuto(tuto);
        setTitle(tuto.title);
        setDescription(tuto.description || '');
        setYoutubeLink(tuto.youtube_link);
        setFormError('');
        onOpen();
    };

    const handleSave = () => {
        if (!title.trim()) {
            setFormError(t("Veuillez entrer un titre"));
            return;
        }
        if (!youtubeLink.trim() || !validateYoutubeLink(youtubeLink)) {
            setFormError(t("Veuillez entrer un lien YouTube valide"));
            return;
        }

        const payload = {
            title: title.trim(),
            description: description.trim() || null,
            youtube_link: youtubeLink.trim()
        };

        if (isEditMode && selectedTuto) {
            axiosClient.patch(`/tutoriels/${selectedTuto.id}/`, payload)
                .then(() => {
                    toast({
                        description: t("Tutoriel modifié avec succès"),
                        status: 'success',
                        duration: 2000,
                        isClosable: true,
                        variant: 'left-accent',
                        position: 'bottom-right'
                    });
                    fetchTutoriels();
                    onClose();
                })
                .catch((err) => {
                    setFormError(err.response?.data?.detail || t("Une erreur est survenue"));
                });
        } else {
            axiosClient.post('/tutoriels/', payload)
                .then(() => {
                    toast({
                        description: t("Tutoriel ajouté avec succès"),
                        status: 'success',
                        duration: 2000,
                        isClosable: true,
                        variant: 'left-accent',
                        position: 'bottom-right'
                    });
                    fetchTutoriels();
                    onClose();
                })
                .catch((err) => {
                    setFormError(err.response?.data?.detail || t("Une erreur est survenue"));
                });
        }
    };

    const handleDelete = () => {
        if (!selectedTutoId) return;
        axiosClient.delete(`/tutoriels/${selectedTutoId}/`)
            .then(() => {
                toast({
                    description: t("Tutoriel supprimé avec succès"),
                    status: 'warning',
                    duration: 2000,
                    isClosable: true,
                    position: 'bottom-right',
                    variant: 'left-accent',
                });
                fetchTutoriels();
            })
            .catch(() => {
                toast({
                    description: t("Une erreur est survenue lors de la suppression"),
                    status: 'error',
                    duration: 2000,
                    isClosable: true,
                    position: 'bottom-right',
                    variant: 'left-accent',
                });
            });
        setIsAlertOpen(false);
    };

    return (
        <Box position="relative" mb={5}>
            {/* Floating Add Button */}
            <Flex zIndex={999} position="fixed" bottom={10} right={5}>
                <Button
                    onClick={handleOpenAddModal}
                    colorScheme='purple'
                    shadow="lg"
                    borderRadius="full"
                    h={14}
                    w={14}
                >
                    <PiPlusBold size={20} />
                </Button>
            </Flex>

            {/* Breadcrumbs */}
            <Flex>
                <Box w="100%" display="flex" justifyContent="space-between" flexDirection={{ base: 'column', md: "row" }} mt={2} mb={{ base: 0, md: 5 }}>
                    <Breadcrumb fontFamily='Plus Jakarta Display' fontStyle="italic" fontSize={{ base: "20px", sm: "25px", lg: "27px" }} color="gray">
                        <BreadcrumbItem>
                            <BreadcrumbLink onClick={() => navigate('/')}>{t("Accueil")}</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbItem isCurrentPage>
                            <BreadcrumbLink>{t('Tutoriels')}</BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>
                </Box>
            </Flex>

            {/* Table layout */}
            <Flex h="100%">
                <Flex
                    px={5}
                    py={5}
                    bg={useColorModeValue('white', 'gray.700')}
                    rounded="2xl"
                    mt={2}
                    direction="column"
                    w="100%"
                    h="fit-content"
                    shadow="sm"
                >
                    {/* Search bar */}
                    <Flex justify="flex-end" mb={4}>
                        <InputGroup size="md" w={{ base: '100%', lg: "30%" }}>
                            <InputLeftElement pointerEvents='none'>
                                <SearchIcon color='gray.300' />
                            </InputLeftElement>
                            <Input
                                borderRadius="xl"
                                placeholder={t("Recherche...")}
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(0);
                                }}
                            />
                        </InputGroup>
                    </Flex>

                    {/* Table */}
                    <TableContainer>
                        <Table variant='simple'>
                            <Thead>
                                <Tr>
                                    <Th borderBottomColor='gray.400'>{t('Titre')}</Th>
                                    <Th borderBottomColor='gray.400'>{t('Lien YouTube')}</Th>
                                    <Th borderBottomColor='gray.400'>{t('Date Création')}</Th>
                                    <Th isNumeric borderBottomColor='gray.400'></Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {loadingData ? (
                                    <Tr>
                                        <Td><Skeleton height='20px' /></Td>
                                        <Td><Skeleton height='20px' /></Td>
                                        <Td><Skeleton height='20px' /></Td>
                                        <Td><Skeleton height='20px' /></Td>
                                    </Tr>
                                ) : data.length > 0 ? (
                                    data.map((row) => (
                                        <Tr key={row.id}>
                                            <Td fontWeight="medium" maxW="200px" isTruncated>{row.title}</Td>
                                            <Td maxW="250px" isTruncated color="purple.500">
                                                <a href={row.youtube_link} target="_blank" rel="noopener noreferrer">
                                                    {row.youtube_link}
                                                </a>
                                            </Td>
                                            <Td>{new Date(row.date_creation).toLocaleDateString('fr-FR')}</Td>
                                            <Td isNumeric>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleOpenEditModal(row)}
                                                    variant='outline'
                                                    colorScheme='whatsapp'
                                                    leftIcon={<EditIcon />}
                                                    mr={3}
                                                    borderRadius="lg"
                                                >
                                                    {t("Modifier")}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedTutoId(row.id);
                                                        setIsAlertOpen(true);
                                                    }}
                                                    variant='outline'
                                                    colorScheme='red'
                                                    leftIcon={<DeleteIcon />}
                                                    borderRadius="lg"
                                                >
                                                    {t("Supprimer")}
                                                </Button>
                                            </Td>
                                        </Tr>
                                    ))
                                ) : (
                                    <Tr>
                                        <Td colSpan={4} textAlign="center">
                                            {t("Aucune ligne correspondante n'a été trouvée.")}
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <Flex mt={4} justify="space-between" align="center" w="100%">
                        <Box flex="1">
                            <MyPagination
                                paginationData={paginationData}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                PAGE_SIZE={pageSize}
                            />
                        </Box>
                    </Flex>
                </Flex>
            </Flex>

            {/* Create/Edit Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent borderRadius="2xl">
                    <ModalHeader fontSize="2xl" fontWeight="bold">
                        {isEditMode ? t("Modifier le tutoriel") : t("Ajouter un tutoriel")}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {formError && (
                            <Alert mb={4} fontSize="sm" variant="left-accent" status='error' rounded="md">
                                <AlertIcon />
                                {formError}
                            </Alert>
                        )}
                        <Stack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel fontWeight="semibold">{t("Titre du tutoriel")}</FormLabel>
                                <Input
                                    borderRadius="xl"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder={t("Ex: Comment importer vos commandes via Excel")}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontWeight="semibold">{t("Description du tutoriel")}</FormLabel>
                                <Textarea
                                    borderRadius="xl"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={t("Ex: Ce tutoriel explique pas à pas comment formater votre Excel...")}
                                    rows={4}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontWeight="semibold">{t("Lien YouTube")}</FormLabel>
                                <Input
                                    borderRadius="xl"
                                    value={youtubeLink}
                                    onChange={(e) => setYoutubeLink(e.target.value)}
                                    placeholder="Ex: https://www.youtube.com/watch?v=..."
                                />
                            </FormControl>
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose} borderRadius="xl">
                            {t("Annuler")}
                        </Button>
                        <Button colorScheme="purple" onClick={handleSave} borderRadius="xl" shadow="md">
                            {isEditMode ? t("Modifier") : t("Ajouter")}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete Alert Dialog */}
            <AlertDialog
                isOpen={isAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => setIsAlertOpen(false)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius="2xl">
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            {t("Supprimer le tutoriel")}
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            {t("Êtes-vous sûr de vouloir supprimer ce tutoriel ?")}
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setIsAlertOpen(false)} borderRadius="xl">
                                {t("Annuler")}
                            </Button>
                            <Button colorScheme='red' onClick={handleDelete} ml={3} borderRadius="xl" shadow="md">
                                {t("Supprimer")}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default GestTutorielsAdmin;
